import { flexRender, Table } from "@tanstack/react-table";
import {
  Table as DataTable,
  TableBody as DataTableBody,
  TableHeader as DataTableHeader,
  TableCell,
  TableRow,
  TableHead,
} from "@/components/ui/table";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import React from "react";
import { cn } from "@/utils/classnames";

interface MainTableProps<TData> {
  table: Table<TData>;
  onRowClick?: (row: TData) => void;
  isLoading: boolean;
}

const MainTable = <TData,>(
  { table, onRowClick, isLoading }: MainTableProps<TData>,
  ref: React.ForwardedRef<HTMLTableElement>
) => {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <DataTable ref={ref}>
        <DataTableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="hover:bg-skin-primary bg-skin-primary"
            >
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                    className="h-11"
                    //   colSpan={header.colSpan}
                  >
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <div
                        className={cn(
                          "group flex h-full cursor-pointer select-none items-center justify-between gap-2 text-white hover:text-white/80 break-keep",
                          {
                            "cursor-pointer": header.column.getCanSort(),
                          }
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                          // Enhanced keyboard handling for sorting
                          if (
                            header.column.getCanSort() &&
                            (e.key === "Enter" || e.key === " ")
                          ) {
                            e.preventDefault();
                            header.column.getToggleSortingHandler()?.(e);
                          }
                        }}
                        tabIndex={header.column.getCanSort() ? 0 : undefined}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: (
                            <ChevronUp
                              className="shrink-0 "
                              size={16}
                              strokeWidth={2}
                              aria-hidden="true"
                            />
                          ),
                          desc: (
                            <ChevronDown
                              className="shrink-0 "
                              size={16}
                              strokeWidth={2}
                              aria-hidden="true"
                            />
                          ),
                        }[header.column.getIsSorted() as "asc" | "desc"] ??
                          null}
                        {
                          <ChevronUp
                            className={cn("shrink-0 hidden", {
                              "group-hover:inline-block":
                                !header.column.getIsSorted(),
                            })}
                            size={16}
                            strokeWidth={2}
                            aria-hidden="true"
                          />
                        }
                      </div>
                    ) : (
                      <div className="text-white break-keep">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </DataTableHeader>
        <DataTableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                onClick={() => !!onRowClick && onRowClick(row.original)}
                className={cn({ "hover:cursor-pointer": !!onRowClick })}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="last:py-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : !table.getRowModel().rows?.length && isLoading ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 justify-items-center"
              >
                <Loader2 className="w-9 h-9 animate-spin" />
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                No results.
              </TableCell>
            </TableRow>
          )}
        </DataTableBody>
      </DataTable>
    </div>
  );
};

// Wrap with forwardRef and infer TData
const MainTableWithRef = React.forwardRef(MainTable) as <TData>(
  props: MainTableProps<TData> & { ref?: React.ForwardedRef<HTMLTableElement> }
) => ReturnType<typeof MainTable>;

MainTable.displayName = "MainTable";

export { MainTableWithRef as MainTable };
export default MainTableWithRef;
